import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  Download,
  FileText,
  Loader2,
  MessageSquare,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { trpc } from "@/lib/trpc";
import { useDateFnsLocale } from "@/lib/format-dates";
import { useRole } from "@/hooks/use-role";
import { cn } from "@/lib/utils";
import type { OrderMessage, OrderMessageSide } from "@threadheads/shared";

// KEEP IN LOCKSTEP (attachment caps) with order-messages.service.ts and the
// router zod schema — the aggregate cap keeps the base64 payload under the
// global 10mb express.json limit.
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 6 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
]);
const POLL_MS = 30_000;

interface PendingAttachment {
  fileName: string;
  contentType: string;
  /** base64 (no data: prefix) */
  body: string;
  sizeBytes: number;
  /** object URL for image preview */
  previewUrl: string | null;
}

interface OrderChatPanelProps {
  /** Which side of the 3PL relationship the CALLER is on — picks the tRPC
   *  procedure pair and the bubble alignment. */
  mode: OrderMessageSide;
  shopifyOrderId: number;
  /** Owner mode only: the client org whose order this is. */
  clientOrganizationId?: string;
  /** Unread count for this thread at open time (drives the "New" divider). */
  initialUnread?: number;
  className?: string;
}

const initials = (name: string | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * BLU-665 Phase 3 — the per-order 3PL↔client conversation. One component for
 * both sides: the pure client's detail pane (`mode="client"`) and the owner's
 * expanded order row on /production/orders (`mode="owner"`). Polls the open
 * thread every 30s (no realtime in the app yet), marks the caller's
 * order_message notifications read on load, and supports image/PDF
 * attachments (signed URLs, short TTL). Composer is permission-gated on
 * orders.messages_write (RBAC layer 1 of 4 for the write; the backend
 * handler is the enforcement layer).
 */
export function OrderChatPanel({
  mode,
  shopifyOrderId,
  clientOrganizationId,
  initialUnread = 0,
  className,
}: OrderChatPanelProps) {
  const { t } = useTranslation("clientOrders");
  const { can } = useRole();
  const canWrite = can("orders.messages_write");
  const utils = trpc.useUtils();

  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Snapshot the unread count at mount — the divider position must not move
  // when markThreadRead resets the live count.
  const unreadAtOpen = useRef(initialUnread);

  const clientQuery = trpc.orderMessages.list.useQuery(
    { shopifyOrderId },
    {
      enabled: mode === "client",
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
      retry: (count, error) =>
        error.data?.code === "PRECONDITION_FAILED" ? false : count < 2,
    },
  );
  const ownerQuery = trpc.orderMessages.ownerList.useQuery(
    { shopifyOrderId, clientOrganizationId: clientOrganizationId ?? "" },
    {
      enabled: mode === "owner" && !!clientOrganizationId,
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
      retry: (count, error) =>
        error.data?.code === "PRECONDITION_FAILED" ? false : count < 2,
    },
  );
  const threadQuery = mode === "client" ? clientQuery : ownerQuery;
  const thread = threadQuery.data;

  const markRead = trpc.orderMessages.markThreadRead.useMutation({
    onSuccess: () => {
      void utils.orderMessages.inboxMeta.invalidate();
      void utils.notifications.unreadCount.invalidate();
    },
  });
  // Mark this thread's notifications read once per thread load.
  const markedOrderRef = useRef<string | null>(null);
  useEffect(() => {
    if (!thread?.orderId || markedOrderRef.current === thread.orderId) return;
    markedOrderRef.current = thread.orderId;
    markRead.mutate({ orderId: thread.orderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.orderId]);

  const postClient = trpc.orderMessages.post.useMutation();
  const postOwner = trpc.orderMessages.ownerPost.useMutation();
  const postMutation = mode === "client" ? postClient : postOwner;

  // Pin scroll to the newest message whenever the count changes.
  const messageCount = thread?.messages.length ?? 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messageCount]);

  // Revoke preview object URLs on unmount.
  useEffect(
    () => () => {
      setPending((current) => {
        current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
        return current;
      });
    },
    [],
  );

  const newDividerIndex = useMemo(() => {
    if (!thread || unreadAtOpen.current <= 0) return -1;
    // The divider sits before the last `unreadAtOpen` messages from the other
    // side (an approximation of "first unseen" — notifications don't carry
    // message ids).
    let remaining = unreadAtOpen.current;
    for (let i = thread.messages.length - 1; i >= 0; i -= 1) {
      if (thread.messages[i]!.side !== mode) {
        remaining -= 1;
        if (remaining === 0) return i;
      }
    }
    return -1;
  }, [thread, mode]);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    const next: PendingAttachment[] = [];
    for (const file of Array.from(files)) {
      if (pending.length + next.length >= MAX_ATTACHMENTS) {
        toast({ title: t("chat.tooManyAttachments"), variant: "destructive" });
        break;
      }
      if (!ALLOWED_TYPES.has(file.type)) {
        toast({ title: t("chat.unsupportedType"), variant: "destructive" });
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast({ title: t("chat.attachmentTooLarge"), variant: "destructive" });
        continue;
      }
      const totalSoFar = [...pending, ...next].reduce(
        (sum, a) => sum + a.sizeBytes,
        0,
      );
      if (totalSoFar + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        toast({
          title: t("chat.attachmentsTotalTooLarge"),
          variant: "destructive",
        });
        continue;
      }
      try {
        const body = await fileToBase64(file);
        next.push({
          fileName: file.name,
          contentType: file.type,
          body,
          sizeBytes: file.size,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
        });
      } catch {
        toast({ title: t("chat.readFailed"), variant: "destructive" });
      }
    }
    if (next.length > 0) setPending((current) => [...current, ...next]);
  };

  const removePending = (index: number) => {
    setPending((current) => {
      const target = current[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((_, i) => i !== index);
    });
  };

  const send = () => {
    const body = draft.trim();
    if ((!body && pending.length === 0) || postMutation.isPending) return;
    const attachments = pending.map(({ fileName, contentType, body: b }) => ({
      fileName,
      contentType,
      body: b,
    }));
    const onSuccess = () => {
      setDraft("");
      setPending((current) => {
        current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
        return [];
      });
      void threadQuery.refetch();
      void utils.orderMessages.inboxMeta.invalidate();
      // Refreshes the owner inbox preview; a no-op where it is unobserved.
      void utils.orderMessages.ownerThreads.invalidate();
    };
    const onError = (error: { message: string }) => {
      toast({
        title: t("chat.sendFailed"),
        description: error.message,
        variant: "destructive",
      });
    };
    if (mode === "client") {
      postClient.mutate({ shopifyOrderId, body, attachments }, { onSuccess, onError });
    } else {
      postOwner.mutate(
        {
          shopifyOrderId,
          clientOrganizationId: clientOrganizationId ?? "",
          body,
          attachments,
        },
        { onSuccess, onError },
      );
    }
  };

  const handleComposerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      send();
    }
  };

  // Pre-sync orders can't host a thread yet (no ERP orders.id).
  const unavailable =
    threadQuery.isError &&
    threadQuery.error.data?.code === "PRECONDITION_FAILED";

  // A half-written reply or a pending attachment blocks the version-skew
  // auto-reload (ProductionLayout runs it in auto-reload mode), which would
  // otherwise wipe the draft on deploy. A hidden tab still reloads regardless.
  const hasUnsentWork = draft.trim() !== "" || pending.length > 0;

  return (
    <div
      className={cn("flex min-h-0 flex-col", className)}
      data-reload-blocked={hasUnsentWork ? "" : undefined}
    >
      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
        {threadQuery.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <Skeleton className="h-7 w-7 rounded-full" />
                <Skeleton className="h-14 w-2/3 rounded-xl" />
              </div>
            ))}
          </div>
        ) : unavailable ? (
          <ChatEmptyState text={t("chat.unavailableUntilSynced")} />
        ) : threadQuery.isError ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">{t("chat.loadFailed")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void threadQuery.refetch()}
            >
              {t("chat.retry")}
            </Button>
          </div>
        ) : messageCount === 0 ? (
          <ChatEmptyState
            text={
              mode === "client"
                ? t("chat.emptyClient")
                : t("chat.emptyOwner")
            }
          />
        ) : (
          <ul className="space-y-3">
            {thread!.messages.map((message, index) => (
              <li key={message.id}>
                {index === newDividerIndex ? (
                  <div
                    className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-primary"
                    aria-label={t("chat.newDivider")}
                  >
                    <span className="h-px flex-1 bg-primary/40" />
                    {t("chat.newDivider")}
                    <span className="h-px flex-1 bg-primary/40" />
                  </div>
                ) : null}
                <MessageBubble message={message} own={message.side === mode} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Composer */}
      {canWrite && !unavailable ? (
        <div className="shrink-0 border-t px-3 py-2.5 sm:px-4">
          {pending.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-2">
              {pending.map((att, index) => (
                <span
                  key={`${att.fileName}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded-md border bg-muted px-2 py-1 text-xs"
                >
                  {att.previewUrl ? (
                    <img
                      src={att.previewUrl}
                      alt=""
                      className="h-6 w-6 rounded object-cover"
                    />
                  ) : (
                    <FileText
                      className="h-3.5 w-3.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  <span className="max-w-[140px] truncate">{att.fileName}</span>
                  <button
                    type="button"
                    onClick={() => removePending(index)}
                    aria-label={t("chat.removeAttachment", {
                      name: att.fileName,
                    })}
                    className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2 rounded-xl border bg-muted/30 p-1.5 pl-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder={
                mode === "client"
                  ? t("chat.placeholder")
                  : t("chat.placeholderOwner")
              }
              aria-label={t("chat.composerAria")}
              rows={1}
              className="max-h-28 min-h-9 flex-1 resize-none border-0 bg-transparent p-1 text-sm shadow-none focus-visible:ring-0"
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
              className="hidden"
              onChange={(event) => {
                void addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label={t("chat.attachAria")}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label={t("chat.sendAria")}
              disabled={
                postMutation.isPending || (!draft.trim() && pending.length === 0)
              }
              onClick={send}
            >
              {postMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>
          <p className="px-1 pt-1 text-[10.5px] text-muted-foreground">
            {t("chat.sendHint")}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function ChatEmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 py-8 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <MessageSquare className="h-5 w-5" aria-hidden="true" />
      </div>
      <p className="max-w-[280px] text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function MessageBubble({
  message,
  own,
}: {
  message: OrderMessage;
  own: boolean;
}) {
  const { t } = useTranslation("clientOrders");
  const dateLocale = useDateFnsLocale();
  const fallbackName = t("card.previewPartner");
  return (
    <div className={cn("flex max-w-[88%] items-start gap-2.5", own && "ml-auto flex-row-reverse")}>
      <span
        aria-hidden="true"
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
          own
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground ring-1 ring-border",
        )}
      >
        {initials(message.authorName)}
      </span>
      <div
        className={cn(
          "min-w-0 rounded-xl px-3 py-2 text-sm",
          own
            ? "rounded-tr-sm bg-primary text-primary-foreground"
            : "rounded-tl-sm border bg-muted/60",
        )}
      >
        <p
          className={cn(
            "text-[11px] font-semibold",
            own ? "text-primary-foreground/85" : "text-foreground",
          )}
        >
          {message.authorName ?? fallbackName}
        </p>
        {message.body ? (
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        ) : null}
        {message.attachments.map((att) => {
          const isImage = !!att.contentType?.startsWith("image/");
          // In own (bg-primary) bubbles the default ring-ring is invisible.
          const focusRing = cn(
            "focus-visible:outline-none focus-visible:ring-2",
            own ? "focus-visible:ring-primary-foreground" : "focus-visible:ring-ring",
          );
          // The signature covers only path + expiry, so appending `&download=`
          // equals what storage-js builds; Storage then answers with
          // Content-Disposition: attachment (`<a download>` is ignored cross-origin).
          const downloadHref = att.url
            ? `${att.url}&download=${encodeURIComponent(att.fileName)}`
            : null;
          const downloadLabel = t("chat.downloadAria", { name: att.fileName });

          if (isImage && att.url && downloadHref) {
            return (
              <div key={att.id}>
                <a
                  href={att.url}
                  target="_blank"
                  rel="noreferrer"
                  className={cn("mt-1.5 block rounded-lg", focusRing)}
                >
                  <img
                    src={att.url}
                    alt={att.fileName}
                    loading="lazy"
                    className="max-h-44 rounded-lg border object-contain"
                  />
                </a>
                <a
                  href={downloadHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={downloadLabel}
                  className={cn(
                    "mt-1 inline-flex min-h-6 items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium",
                    focusRing,
                    own
                      ? "bg-primary-foreground text-primary-strong hover:bg-primary-foreground/90"
                      : "text-muted-foreground hover:bg-background hover:text-foreground",
                  )}
                >
                  <Download className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {t("chat.download")}
                </a>
              </div>
            );
          }

          return (
            <div
              key={att.id}
              className={cn(
                "mt-1.5 flex items-center gap-1 rounded-lg border text-xs",
                own
                  ? "border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground"
                  : "bg-background",
              )}
            >
              <a
                href={att.url ?? undefined}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-1.5",
                  focusRing,
                  !att.url && "pointer-events-none opacity-60",
                )}
              >
                <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{att.fileName}</span>
              </a>
              {downloadHref ? (
                <a
                  href={downloadHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={downloadLabel}
                  title={t("chat.download")}
                  className={cn(
                    "ml-auto mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                    focusRing,
                    own
                      ? "bg-primary-foreground text-primary-strong hover:bg-primary-foreground/90"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              ) : null}
            </div>
          );
        })}
        <p
          className={cn(
            "mt-1 text-[10.5px]",
            own ? "text-primary-foreground/70" : "text-muted-foreground",
          )}
        >
          {format(new Date(message.createdAt), "d MMM, HH:mm", {
            locale: dateLocale,
          })}
        </p>
      </div>
    </div>
  );
}
